import './userHoursOverlay.css';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  PAY_FREQUENCIES,
  TIME_OFF_TYPES,
  holidayLabel,
  isHolidayOn,
  nextHolidayDate,
  userHoursSchema,
  type HoursMode,
  type Pay,
  type TimeOff,
  type Timesheet,
  type UserHours,
} from '@ehr/shared';
import { api } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';
import { DateField, TimeField } from '../components/DateTimeField.js';
import { Dd } from '../components/Dropdown.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import {
  DAY_NAMES,
  DAY_ORDER,
  MODE_LABELS,
  PAY_FREQ_LABEL,
  PAY_PERIOD_LABEL,
  addTimeOff,
  clockOutEntry,
  computeComp,
  dayTotal,
  defaultHours,
  defaultPay,
  defaultTimesheet,
  firstHoursError,
  fmtClock,
  fmtDate,
  fmtDayLabel,
  fmtElapsed,
  fmtMoney,
  fmtMonth,
  localDate,
  localHhmm,
  monthGrid,
  monthTotal,
  shiftMonth,
  sortEntries,
  timeOffDraftError,
  weekSummary,
  weekTotal,
  type DayKey,
  type TimeOffDraft,
  type UserStatus,
} from '../lib/userHours.js';
import { IconX, color, initials, msg, uname, type Holiday, type TeamUser } from './usersShared.js';

// ── Hours: the mockup's "User Operational Hours" overlay ─────────────────────
/**
 * A shift / break / meal time: type it ("9", "930p", "14:00") or pick it from the clock
 * list (ehr#60 - every time box takes both). The shared TimeField, in the compact size.
 */
function UhTime({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <TimeField
      className="uh-time"
      value={value}
      onChange={onChange}
      ariaLabel={label}
      placeholder="--:-- --"
    />
  );
}

/**
 * A number box bound to a schema amount (a number, or '' when blank). Commits valid
 * values as you type and clamps to [min, max] on blur, so the draft always validates.
 */
function NumInput({
  value,
  onChange,
  label,
  min = 0,
  max = 1_000_000,
  step,
  placeholder,
  className = 'uh-in',
  id,
}: {
  value: number | '';
  onChange: (v: number | '') => void;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  className?: string;
  id?: string;
}) {
  const show = (v: number | '') => (v === '' ? '' : String(v));
  const [text, setText] = useState(show(value));
  useEffect(() => {
    const cur = text.trim() === '' ? '' : Number(text);
    if (cur !== value) setText(show(value));
    // Only resync when the stored value changes from outside (not mid-typing "1.").
  }, [value]);
  return (
    <input
      id={id}
      className={className}
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      aria-label={label}
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === '') return onChange('');
        const n = Number(t);
        if (Number.isFinite(n) && n >= min && n <= max) onChange(n);
      }}
      onBlur={() => {
        if (text.trim() === '') return onChange('');
        const n = Number(text);
        if (!Number.isFinite(n)) return setText(show(value));
        const c = Math.min(max, Math.max(min, n));
        setText(String(c));
        onChange(c);
      }}
    />
  );
}

type Tab = HoursMode | 'timeOff' | 'pay' | 'timesheet';
const EXTRA_TABS: { id: Exclude<Tab, HoursMode>; label: string }[] = [
  { id: 'timeOff', label: 'Time Off' },
  { id: 'pay', label: 'Pay' },
  { id: 'timesheet', label: 'Timesheet' },
];
const isMode = (t: Tab): t is HoursMode => t in MODE_LABELS;

export function HoursOverlay({
  user,
  roleLabel,
  status,
  onBack,
  onSaved,
  holidays = [],
  wizard,
}: {
  user: TeamUser;
  roleLabel: string;
  status: UserStatus;
  onBack: () => void;
  onSaved: () => void;
  /** Org-wide company holidays, listed read-only on the Time Off tab. */
  holidays?: Holiday[];
  /** Set when opened from Add User: step 2 of 3 footer (Back / Skip / Save & Next). */
  wizard?: { onBack: () => void; onSkip: () => void };
}) {
  const [h, setH] = useState<UserHours>(() =>
    user.hours ? structuredClone(user.hours) : defaultHours(),
  );
  const [tab, setTab] = useState<Tab>('operational');
  const mode: HoursMode = isMode(tab) ? tab : 'operational';
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const w = h.modes[mode];
  const sum = useMemo(() => weekSummary(w), [w]);

  // ehr#211: leaving with unsaved changes (click outside, ×, Escape, wizard Back) asks
  // Save / Don't Save / Cancel, like the client record (ehr#180). "Unsaved" = the hours
  // differ from what the window opened with (every tab here saves together).
  const opened = useRef(JSON.stringify(h));
  const dirty = JSON.stringify(h) !== opened.current;
  const [leaveTo, setLeaveTo] = useState<null | (() => void)>(null);
  const leave = (go: () => void) => {
    if (dirty && !saving) setLeaveTo(() => go);
    else go();
  };
  const leaveRef = useRef(leave);
  leaveRef.current = leave;
  const askingRef = useRef(false);
  askingRef.current = leaveTo !== null;
  useEffect(() => {
    // Window capture runs before the Users window's own Escape (document capture), so a
    // dirty Hours window asks first instead of closing.
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.target instanceof Element && e.target.closest('.dd-open, .dtf-open')) return;
      if (askingRef.current) {
        e.stopPropagation();
        setLeaveTo(null);
        return;
      }
      // The Credify confirm (Clear hours, remove time off: ehr#333) handles its own Esc.
      if (document.querySelector('.cred-confirm-overlay')) return;
      if (JSON.stringify(hRef.current) === opened.current) return;
      e.stopPropagation();
      e.preventDefault();
      leaveRef.current(onBack);
    };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [onBack]);
  const hRef = useRef(h);
  hRef.current = h;

  const setDay = (d: DayKey, fn: (day: UserHours['modes']['operational']['0']) => void) =>
    setH((prev) => {
      const next = structuredClone(prev);
      fn(next.modes[mode][d]);
      return next;
    });
  const copyMonday = () =>
    setH((prev) => {
      const next = structuredClone(prev);
      const mon = next.modes[mode]['1'];
      for (const d of DAY_ORDER) if (d !== '1') next.modes[mode][d] = structuredClone(mon);
      return next;
    });
  const save = () => {
    const e = firstHoursError(h);
    if (e) return setErr(e);
    const parsed = userHoursSchema.safeParse(h);
    if (!parsed.success) return setErr(parsed.error.issues[0]?.message ?? 'Check the hours');
    setSaving(true);
    setErr(null);
    api
      .put(`/settings/users/${user.id}/hours`, { hours: h })
      .then(onSaved)
      .catch((x: unknown) => setErr(msg(x)))
      .finally(() => setSaving(false));
  };
  const clear = async () => {
    if (
      !(await askConfirm({ message: `Clear ${uname(user)}’s hours?`, confirmLabel: 'Clear hours' }))
    )
      return;
    setSaving(true);
    api
      .put(`/settings/users/${user.id}/hours`, { hours: null })
      .then(onSaved)
      .catch((x: unknown) => setErr(msg(x)))
      .finally(() => setSaving(false));
  };

  const pair = (
    d: DayKey,
    label: string,
    r: { from: string; to: string },
    set: (k: 'from' | 'to', v: string) => void,
  ) => (
    <div className="uh-row">
      <span className="uh-row-label">{label}</span>
      <div className="uh-row-times">
        <UhTime
          value={r.from}
          onChange={(v) => set('from', v)}
          label={`${DAY_NAMES[d]} ${label} start`}
        />
        <span className="uh-to">to</span>
        <UhTime
          value={r.to}
          onChange={(v) => set('to', v)}
          label={`${DAY_NAMES[d]} ${label} end`}
        />
      </div>
    </div>
  );

  // ehr#203: shown in the footer, next to Save / Save & Next, so it is always in view.
  const errBox = err && (
    <span className="foot-err" role="alert">
      {err}
    </span>
  );
  const tabBtn = (id: Tab, label: string) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={`uh-mode-btn${tab === id ? ' active' : ''}`}
      onClick={() => setTab(id)}
    >
      {label}
    </button>
  );

  let body;
  if (tab === 'timeOff') {
    body = (
      <TimeOffTab
        list={h.timeOff}
        onChange={(timeOff) => setH((prev) => ({ ...prev, timeOff }))}
        holidays={holidays}
      />
    );
  } else if (tab === 'pay') {
    body = <PayTab h={h} onChange={(pay) => setH((prev) => ({ ...prev, pay }))} />;
  } else if (tab === 'timesheet') {
    body = (
      <TimesheetTab
        ts={h.timesheet ?? defaultTimesheet()}
        onChange={(timesheet) => setH((prev) => ({ ...prev, timesheet }))}
        timeOff={h.timeOff}
        holidays={holidays}
      />
    );
  } else {
    body = (
      <>
        <div className="uh-eff">
          <span className="uh-eff-label">Schedule effective</span>
          <span className="uh-eff-date">
            <DateField value={h.effectiveDate} onChange={(v) => setH({ ...h, effectiveDate: v })} />
          </span>
          {h.effectiveDate && (
            <button
              className="uh-datepick-clear"
              title="Clear"
              onClick={() => setH({ ...h, effectiveDate: '' })}
            >
              ×
            </button>
          )}
          <span className="uh-eff-hint">optional — leave blank to apply always</span>
        </div>
        <div className="uh-toolbar">
          <span className="uh-toolbar-label">
            {MODE_LABELS[mode].toUpperCase()} · {sum.days} {sum.days === 1 ? 'DAY' : 'DAYS'}{' '}
            AVAILABLE · ~{sum.hours.toFixed(1)} HRS/WK
          </span>
          <button className="btn uh-copy" onClick={copyMonday}>
            Copy Monday to all days
          </button>
        </div>
        <div className="uh-grid">
          {DAY_ORDER.map((d) => {
            const day = w[d];
            return (
              <div key={d} className={`uh-card${day.on ? ' on' : ''}`}>
                <div className="uh-card-head">
                  <span className="uh-day">{DAY_NAMES[d]}</span>
                  <label className="uh-switch" title={day.on ? 'Available' : 'Unavailable'}>
                    <input
                      type="checkbox"
                      checked={day.on}
                      onChange={(e) => setDay(d, (x) => void (x.on = e.target.checked))}
                      aria-label={`${DAY_NAMES[d]} available`}
                    />
                    <span />
                  </label>
                </div>
                {!day.on ? (
                  <div className="uh-off">Closed / unavailable</div>
                ) : (
                  <div className="uh-rows">
                    {day.shifts.map((s, i) => (
                      <div key={`s${i}`}>
                        {pair(d, day.shifts.length > 1 ? `Shift ${i + 1}` : 'Shift', s, (k, v) =>
                          setDay(d, (x) => void (x.shifts[i]![k] = v)),
                        )}
                      </div>
                    ))}
                    <div className="uh-subhead">Breaks</div>
                    {day.breaks.map((b, i) => (
                      <div key={`b${i}`}>
                        {pair(d, `Break ${i + 1}`, b, (k, v) =>
                          setDay(d, (x) => void (x.breaks[i]![k] = v)),
                        )}
                      </div>
                    ))}
                    <div className="uh-subhead">Meal</div>
                    {pair(d, 'Lunch / Dinner', day.meal ?? { from: '', to: '' }, (k, v) =>
                      setDay(d, (x) => {
                        x.meal = { ...(x.meal ?? { from: '', to: '' }), [k]: v };
                      }),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div className="uh-overlay" onMouseDown={(e) => e.target === e.currentTarget && leave(onBack)}>
      <div className="uh-modal" role="dialog" aria-modal="true" aria-label="User Operational Hours">
        <div className="uh-head">
          <div>
            <div className="uh-title">User Operational Hours</div>
            <div className="uh-who">
              <span className="user-avatar" style={{ background: color(user.id) }}>
                {initials(user)}
              </span>
              <div>
                <div className="uh-who-name">{uname(user)}</div>
                <div className="uh-who-sub">
                  {[user.title, roleLabel].filter(Boolean).join(' · ') ||
                    'Set the days and times this user is available'}
                </div>
              </div>
            </div>
            <div className={`uh-today us-${status.tone}`}>
              <span className="us-dot" />
              Today: {status.full}
            </div>
          </div>
          <button className="uh-x" title="Close" onClick={() => leave(onBack)}>
            {IconX}
          </button>
        </div>
        <div className="uh-body">
          <div className="uh-mode-toggle uh-tabs6" role="tablist">
            {(Object.keys(MODE_LABELS) as HoursMode[]).map((m) => tabBtn(m, MODE_LABELS[m]))}
            {EXTRA_TABS.map((t) => tabBtn(t.id, t.label))}
          </div>
          {body}
        </div>
        <div className="uh-foot">
          <span className="uh-foot-note">
            Times use the practice’s time zone. Everything here saves together.
          </span>
          <span className="uh-foot-acts">
            {errBox}
            {wizard ? (
              <>
                <span className="uh-wiz-step">Step 2 of 3 — Hours</span>
                <button className="btn" disabled={saving} onClick={() => leave(wizard.onBack)}>
                  Back
                </button>
                <button className="btn" disabled={saving} onClick={wizard.onSkip}>
                  Skip
                </button>
                <button className="btn primary" disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save & Next — Rights'}
                </button>
              </>
            ) : (
              <>
                {user.hours && (
                  <button className="btn" disabled={saving} onClick={clear}>
                    Clear hours
                  </button>
                )}
                <button className="btn primary" disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </span>
        </div>
      </div>
      {leaveTo && (
        <ConfirmDialog
          message={`You have unsaved changes to ${uname(user)}’s hours. Save them before you leave?`}
          confirmLabel={saving ? 'Saving…' : 'Save'}
          altLabel="Don’t Save"
          cancelLabel="Cancel"
          danger={false}
          busy={saving}
          onClose={() => setLeaveTo(null)}
          onAlt={() => {
            const go = leaveTo;
            setLeaveTo(null);
            go();
          }}
          onConfirm={() => {
            // Save runs its own checks; a problem shows next to Save and the window stays.
            setLeaveTo(null);
            save();
          }}
        />
      )}
    </div>
  );
}

// ── Time Off tab (mockup isTimeOff branch) ───────────────────────────────────
const isTimeOffType = (v: string): v is TimeOff['type'] =>
  (TIME_OFF_TYPES as readonly string[]).includes(v);
const fmtRange = (t: { start: string; end: string }) =>
  t.end !== t.start ? `${fmtDate(t.start)} – ${fmtDate(t.end)}` : fmtDate(t.start);
const emptyDraft = (): TimeOffDraft => ({ start: '', end: '', type: 'Vacation', note: '' });

function TimeOffTab({
  list,
  onChange,
  holidays,
}: {
  list: TimeOff[];
  onChange: (v: TimeOff[]) => void;
  holidays: Holiday[];
}) {
  const uid = useId();
  const [draft, setDraft] = useState<TimeOffDraft>(emptyDraft);
  const [err, setErr] = useState<string | null>(null);
  // ehr#376: soonest first; a one-day holiday already past goes last.
  const hols = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const key = (h: Holiday) => nextHolidayDate(h, today) ?? '9999' + h.date;
    return [...holidays].sort((a, b) => key(a).localeCompare(key(b)));
  }, [holidays]);
  const add = () => {
    const e = timeOffDraftError(draft);
    if (e) return setErr(e);
    setErr(null);
    onChange(addTimeOff(list, draft));
    setDraft(emptyDraft());
  };
  const setStatus = (i: number, s: 'Approved' | 'Denied') =>
    onChange(list.map((t, j) => (j === i ? { ...t, status: t.status === s ? 'Pending' : s } : t)));
  const remove = async (i: number) => {
    const t = list[i];
    if (!t) return;
    const ask = `Remove ${t.type.toLowerCase()} time off (${fmtRange(t)})?`;
    if (!(await askConfirm({ message: ask, confirmLabel: 'Remove' }))) return;
    onChange(list.filter((_, j) => j !== i));
  };
  return (
    <div className="uh-to-wrap">
      <div className="uh-to-list">
        {list.length ? (
          list.map((t, i) => (
            <div className="uh-to-row" key={`${t.start}-${t.end}-${i}`}>
              <span className={`uh-to-badge uh-to-${t.type.toLowerCase()}`}>{t.type}</span>
              <span className="uh-to-dates">{fmtRange(t)}</span>
              <span className="uh-to-note">{t.note}</span>
              <span className={`uh-st uh-st-${t.status.toLowerCase()}`}>{t.status}</span>
              <span className="uh-to-actions">
                <button
                  type="button"
                  className={`uh-st-btn approve${t.status === 'Approved' ? ' on' : ''}`}
                  aria-pressed={t.status === 'Approved'}
                  onClick={() => setStatus(i, 'Approved')}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className={`uh-st-btn deny${t.status === 'Denied' ? ' on' : ''}`}
                  aria-pressed={t.status === 'Denied'}
                  onClick={() => setStatus(i, 'Denied')}
                >
                  Deny
                </button>
              </span>
              <button
                type="button"
                className="uh-to-del"
                title="Remove"
                aria-label={`Remove ${t.type} ${fmtRange(t)}`}
                onClick={() => remove(i)}
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <div className="uh-off uh-off-pad">No time off scheduled yet.</div>
        )}
      </div>
      <div className="uh-to-add">
        <div className="uh-to-add-row uh-top">
          <div className="uh-fld uh-fld-date">
            <label htmlFor={`${uid}-s`}>Start</label>
            <DateField
              id={`${uid}-s`}
              value={draft.start}
              onChange={(v) => setDraft((d) => ({ ...d, start: v }))}
            />
          </div>
          <div className="uh-fld uh-fld-date">
            <label htmlFor={`${uid}-e`}>End</label>
            <DateField
              id={`${uid}-e`}
              value={draft.end}
              min={draft.start || undefined}
              onChange={(v) => setDraft((d) => ({ ...d, end: v }))}
            />
          </div>
          <div className="uh-fld uh-fld-type">
            <span className="uh-fld-label">Type</span>
            <Dd
              floating
              searchable={false}
              ariaLabel="Time off type"
              value={draft.type}
              options={TIME_OFF_TYPES.map((t) => ({ value: t, label: t }))}
              onChange={(v) => isTimeOffType(v) && setDraft((d) => ({ ...d, type: v }))}
            />
          </div>
        </div>
        <div className="uh-to-add-row">
          <div className="uh-fld uh-fld-grow">
            <label htmlFor={`${uid}-n`}>Note</label>
            <input
              id={`${uid}-n`}
              className="uh-in"
              maxLength={300}
              value={draft.note}
              placeholder="Optional note (e.g. covered by Jordan)"
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
          </div>
          <button type="button" className="btn primary" onClick={add}>
            Add Time Off
          </button>
        </div>
        {err && (
          <div className="config-err uh-inline-err" role="alert">
            {err}
          </div>
        )}
      </div>
      <div className="uh-hol">
        <div className="uh-hol-head">
          Company holidays{' '}
          <span className="uh-hol-sub">org-wide — manage in Settings › Company Holidays</span>
        </div>
        <div className="uh-to-list">
          {hols.length ? (
            hols.map((x) => (
              <div className="uh-ch-row" key={`${x.date}-${x.name}`}>
                <span className="uh-ch-date">{holidayLabel(x)}</span>
                <span className="uh-ch-name">{x.name}</span>
              </div>
            ))
          ) : (
            <div className="uh-off uh-off-pad">No company holidays set yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pay tab (mockup isPay branch + uhComputeComp) ────────────────────────────
const isFreq = (v: string): v is Pay['frequency'] =>
  (PAY_FREQUENCIES as readonly string[]).includes(v);

function PayTab({ h, onChange }: { h: UserHours; onChange: (p: Pay) => void }) {
  const uid = useId();
  const pay = h.pay ?? defaultPay();
  const comp = computeComp(h);
  const per = PAY_PERIOD_LABEL[pay.frequency];
  const set = (patch: Partial<Pay>) => onChange({ ...pay, ...patch });
  const setBonus = (i: number, patch: Partial<Pay['bonuses'][number]>) =>
    set({ bonuses: pay.bonuses.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  return (
    <div className="uh-pay">
      <div className="uh-pay-grid">
        <div className="uh-fld">
          <span className="uh-fld-label">Pay basis</span>
          <Dd
            floating
            searchable={false}
            ariaLabel="Pay basis"
            value={pay.basis}
            options={[
              { value: 'hourly', label: 'Hourly' },
              { value: 'daily', label: 'Daily / per-diem' },
            ]}
            onChange={(v) => (v === 'hourly' || v === 'daily') && set({ basis: v })}
          />
        </div>
        <div className="uh-fld">
          <label htmlFor={`${uid}-hr`}>Rate per hour</label>
          <div className="uh-money-in">
            <span aria-hidden="true">$</span>
            <NumInput
              id={`${uid}-hr`}
              className=""
              step={0.01}
              placeholder="0.00"
              value={pay.hourly}
              onChange={(v) => set({ hourly: v })}
            />
          </div>
        </div>
        <div className="uh-fld">
          <label htmlFor={`${uid}-dy`}>Rate per day</label>
          <div className="uh-money-in">
            <span aria-hidden="true">$</span>
            <NumInput
              id={`${uid}-dy`}
              className=""
              step={0.01}
              placeholder="0.00"
              value={pay.daily}
              onChange={(v) => set({ daily: v })}
            />
          </div>
        </div>
        <div className="uh-fld">
          <span className="uh-fld-label">Pay frequency</span>
          <Dd
            floating
            searchable={false}
            ariaLabel="Pay frequency"
            value={pay.frequency}
            options={PAY_FREQUENCIES.map((f) => ({ value: f, label: PAY_FREQ_LABEL[f] }))}
            onChange={(v) => isFreq(v) && set({ frequency: v })}
          />
        </div>
        <div className="uh-fld">
          <label htmlFor={`${uid}-ot`}>Overtime hours (this period)</label>
          <NumInput
            id={`${uid}-ot`}
            step={0.5}
            placeholder="0"
            value={pay.otHours}
            onChange={(v) => set({ otHours: v })}
          />
        </div>
        <div className="uh-fld">
          <label htmlFor={`${uid}-om`}>Overtime multiplier</label>
          <NumInput
            id={`${uid}-om`}
            min={1}
            max={10}
            step={0.05}
            placeholder="1.5"
            value={pay.otMult}
            onChange={(v) => set({ otMult: v })}
          />
        </div>
      </div>
      <div className="uh-bonus">
        <div className="uh-subhead uh-subhead-flat">Bonus tiers · by scheduled hours / week</div>
        <div className="uh-bonus-list">
          {pay.bonuses.length ? (
            pay.bonuses.map((b, i) => (
              <div className="uh-bonus-row" key={i}>
                <span>If</span>
                <NumInput
                  className="uh-in uh-in-sm"
                  label={`Tier ${i + 1} minimum hours per week`}
                  step={0.5}
                  max={168}
                  placeholder="min"
                  value={b.min}
                  onChange={(v) => setBonus(i, { min: v })}
                />
                <span>to</span>
                <NumInput
                  className="uh-in uh-in-sm"
                  label={`Tier ${i + 1} maximum hours per week`}
                  step={0.5}
                  max={168}
                  placeholder="max"
                  value={b.max}
                  onChange={(v) => setBonus(i, { max: v })}
                />
                <span>hrs/wk →</span>
                <NumInput
                  className="uh-in uh-in-sm"
                  label={`Tier ${i + 1} bonus percent`}
                  step={1}
                  max={1000}
                  placeholder="%"
                  value={b.pct}
                  onChange={(v) => setBonus(i, { pct: v })}
                />
                <span>% bonus</span>
                <button
                  type="button"
                  className="uh-to-del"
                  title="Remove"
                  aria-label={`Remove bonus tier ${i + 1}`}
                  onClick={() => set({ bonuses: pay.bonuses.filter((_, j) => j !== i) })}
                >
                  ✕
                </button>
              </div>
            ))
          ) : (
            <div className="uh-off">No bonus tiers yet.</div>
          )}
        </div>
        <button
          type="button"
          className="btn uh-add-tier"
          disabled={pay.bonuses.length >= 20}
          onClick={() => set({ bonuses: [...pay.bonuses, { min: '', max: '', pct: '' }] })}
        >
          + Add bonus tier
        </button>
      </div>
      <div className="uh-comp" aria-live="polite">
        <div className="uh-comp-head">Compensation · {per}</div>
        <div className="uh-comp-grid">
          <div>
            <span>Total scheduled hours / week</span>
            <strong>
              {comp.weeklyH.toFixed(1)} hrs · {comp.days} {comp.days === 1 ? 'day' : 'days'}
            </strong>
          </div>
          <div>
            <span>Base pay ({per})</span>
            <strong>{fmtMoney(comp.periodBase)}</strong>
          </div>
          <div>
            <span>Overtime</span>
            <strong>{fmtMoney(comp.ot)}</strong>
          </div>
          <div>
            <span>Bonus{comp.bonusPct ? ` · ${comp.bonusPct}%` : ''}</span>
            <strong>{fmtMoney(comp.bonus)}</strong>
          </div>
        </div>
        <div className="uh-comp-total">
          <span>Total compensation {per}</span>
          <strong>{fmtMoney(comp.total)}</strong>
        </div>
      </div>
    </div>
  );
}

// ── Timesheet tab (mockup isTimesheet branch + ts* helpers) ──────────────────
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function TimesheetTab({
  ts,
  onChange,
  timeOff,
  holidays,
}: {
  ts: Timesheet;
  onChange: (t: Timesheet) => void;
  timeOff: TimeOff[];
  holidays: Holiday[];
}) {
  const uid = useId();
  const today = localDate(new Date());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [day, setDay] = useState('');
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ts.open) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [ts.open]);

  const clockIn = () => onChange({ ...ts, open: { start: new Date().toISOString() } });
  const clockOut = () => {
    if (!ts.open) return;
    onChange({
      open: null,
      entries: sortEntries([...ts.entries, clockOutEntry(ts.open.start, new Date())]),
    });
  };
  const addManual = () => {
    const n = Number(hours);
    if (!day) return setErr('Tap a day on the calendar first.');
    if (!hours.trim() || !Number.isFinite(n) || n <= 0 || n > 24)
      return setErr('Enter the number of hours worked (up to 24).');
    setErr(null);
    onChange({
      ...ts,
      entries: sortEntries([
        ...ts.entries,
        {
          date: day,
          start: '',
          end: '',
          hours: Math.round(n * 100) / 100,
          note: note.trim(),
          source: 'manual',
        },
      ]),
    });
    setHours('');
    setNote('');
  };
  const nav = (delta: number) => {
    setMonth((m) => shiftMonth(m, delta));
    setDay('');
  };
  const grid = monthGrid(month);
  const since = ts.open ? new Date(ts.open.start) : null;
  const dayEntries = day
    ? ts.entries.map((e, i) => ({ e, i })).filter((x) => x.e.date === day)
    : [];

  return (
    <div className="ts-wrap">
      {since ? (
        <div className="ts-clock ts-clock-on">
          <div className="ts-clock-info">
            <span className="ts-live-dot" aria-hidden="true" />
            Clocked in at {fmtClock(localHhmm(since))}
            <span className="ts-elapsed">
              {' '}
              · {fmtElapsed((now - since.getTime()) / 3_600_000)} so far
            </span>
          </div>
          <button type="button" className="btn primary" onClick={clockOut}>
            Clock Out
          </button>
        </div>
      ) : (
        <div className="ts-clock">
          <div className="ts-clock-info">Not clocked in</div>
          <button type="button" className="btn primary" onClick={clockIn}>
            Clock in
          </button>
        </div>
      )}
      <div className="ts-cal-head">
        <button
          type="button"
          className="ts-nav"
          title="Previous month"
          aria-label="Previous month"
          onClick={() => nav(-1)}
        >
          ‹
        </button>
        <div className="ts-cal-title" aria-live="polite">
          {fmtMonth(month)}
        </div>
        <button
          type="button"
          className="ts-nav"
          title="Next month"
          aria-label="Next month"
          onClick={() => nav(1)}
        >
          ›
        </button>
        <div className="ts-totals">
          <span>
            Month <strong>{monthTotal(ts.entries, month).toFixed(1)}h</strong>
          </span>
          <span>
            This week <strong>{weekTotal(ts.entries, today).toFixed(1)}h</strong>
          </span>
        </div>
      </div>
      <div className="ts-cal">
        {DOW.map((d) => (
          <div className="ts-dow" key={d} aria-hidden="true">
            {d}
          </div>
        ))}
        {Array.from({ length: grid.lead }, (_, i) => (
          <div className="ts-cell ts-empty" key={`e${i}`} aria-hidden="true" />
        ))}
        {grid.days.map((ds) => {
          const hrs = dayTotal(ts.entries, ds);
          const isHol = holidays.some((x) => isHolidayOn(x, ds));
          const isOff = timeOff.some((t) => t.status !== 'Denied' && ds >= t.start && ds <= t.end);
          const cls = ['ts-cell', ds === today && 'ts-today', ds === day && 'ts-sel']
            .filter(Boolean)
            .join(' ');
          const label = [
            fmtDayLabel(ds),
            hrs > 0 && `${Math.round(hrs * 100) / 100} hours`,
            isHol && 'company holiday',
            !isHol && isOff && 'time off',
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <button
              type="button"
              key={ds}
              className={cls}
              aria-pressed={ds === day}
              aria-label={label}
              onClick={() => {
                setDay((cur) => (cur === ds ? '' : ds));
                setErr(null);
              }}
            >
              <span className="ts-dnum">{Number(ds.slice(8))}</span>
              {hrs > 0 && <span className="ts-dhrs">{Math.round(hrs * 10) / 10}h</span>}
              {isHol ? (
                <span className="ts-dmark ts-mark-hol" title="Company holiday" />
              ) : isOff ? (
                <span className="ts-dmark ts-mark-off" title="Time off" />
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="ts-day">
        <div className="uh-subhead uh-subhead-flat">
          {day ? fmtDayLabel(day) : 'Tap a day to view or add hours'}
        </div>
        {day && (
          <>
            <div className="ts-entries">
              {dayEntries.length ? (
                dayEntries.map(({ e, i }) => (
                  <div className="ts-erow" key={`${e.date}-${e.start}-${i}`}>
                    <span className={`ts-esrc ts-esrc-${e.source}`}>
                      {e.source === 'clock' ? 'Clocked' : 'Manual'}
                    </span>
                    <span className="ts-etime">
                      {e.start ? `${fmtClock(e.start)} – ${fmtClock(e.end)}` : 'Manual entry'}
                    </span>
                    <span className="ts-ehrs">{Math.round(e.hours * 100) / 100}h</span>
                    <span className="ts-enote">{e.note}</span>
                    <button
                      type="button"
                      className="uh-to-del"
                      title="Remove"
                      aria-label={`Remove ${e.hours}h entry`}
                      onClick={() =>
                        onChange({ ...ts, entries: ts.entries.filter((_, j) => j !== i) })
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))
              ) : (
                <div className="uh-off">No entries this day.</div>
              )}
            </div>
            <div className="ts-add">
              <div className="uh-fld">
                <label htmlFor={`${uid}-h`}>Hours</label>
                <input
                  id={`${uid}-h`}
                  className="uh-in ts-hours"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={24}
                  step={0.25}
                  placeholder="e.g. 8"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>
              <div className="uh-fld uh-fld-grow">
                <label htmlFor={`${uid}-n`}>Note</label>
                <input
                  id={`${uid}-n`}
                  className="uh-in"
                  maxLength={300}
                  placeholder="Optional"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addManual()}
                />
              </div>
              <button type="button" className="btn primary" onClick={addManual}>
                Add Entry
              </button>
            </div>
          </>
        )}
        {err && (
          <div className="config-err uh-inline-err" role="alert">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
